import React, { useState } from 'react';
import friendlyError from '../../../utils/friendlyError';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FaSearch,
  FaCopy,
  FaCheck,
  FaPaperPlane,
  FaUsers,
  FaSyncAlt,
} from 'react-icons/fa';

import {
  TechSection,
  TechInput,
  TechButton,
  TechButtonGhost,
  StatusPill,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../../styles/techComponents';

import { askCompetitors } from '../../../services/messagingService';
import { CitePopup, wrapWithCitations } from '../../UI/Citations';

const SUGGESTED = [
  '¿Quiénes son los competidores con mayor presencia mediática esta semana?',
  '¿Qué debilidades concretas comparten Manuel Passaglia (HECHOS) y Unión por la Patria que podamos capitalizar?',
  'Hacé una matriz comparativa de fortalezas y debilidades de los 3 principales competidores.',
  '¿Qué temas dominan la cobertura mediática de los competidores en las últimas 4 semanas?',
  '¿Qué movimientos legales o políticos recientes afectan a la familia Passaglia según las noticias indexadas?',
  '¿Qué recomendaciones concretas se desprenden del último insight IA para diferenciarnos del resto?',
];

const Suggestions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Suggestion = styled.button`
  text-align: left;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 5px;
  padding: 0.65rem 0.8rem;
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 0.84rem;
  line-height: 1.45;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: rgba(0, 180, 216, 0.4);
    background: rgba(0, 180, 216, 0.04);
    color: var(--text-primary);
  }

  &::before {
    content: '?';
    color: ${accent};
    font-family: ${mono};
    font-weight: 700;
    margin-right: 0.5rem;
    opacity: 0.7;
  }
`;

const InputBar = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;

  > div { flex: 1; min-width: 240px; }
`;

const ConvoBlock = styled.article`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 35%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${accent};
  border-radius: 6px;
  padding: 1rem 1.15rem;
  margin-bottom: 0.85rem;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px; height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }

  .question {
    font-family: ${mono};
    font-size: 0.7rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${accent};
    margin: 0 0 0.6rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid ${borderSoft};
  }

  .body {
    color: var(--text-secondary);
    font-size: 0.92rem;
    line-height: 1.6;

    h2, h3, h4 { color: var(--text-primary); margin: 1em 0 0.4em; font-weight: 600; }
    h3 { font-size: 0.95em; text-transform: uppercase; letter-spacing: 0.12em; color: ${accent}; }
    p { margin: 0 0 0.6em; }
    ul, ol { margin: 0 0 0.7em 1.3em; padding: 0; }
    li { margin-bottom: 0.25em; }
    strong { color: var(--text-primary); }
    table {
      border-collapse: collapse;
      margin: 0.7em 0;
      font-size: 0.85em;
      width: 100%;
    }
    th, td {
      padding: 6px 10px;
      border: 1px solid ${borderSoft};
      text-align: left;
      vertical-align: top;
    }
    th {
      background: ${panelDeep};
      font-family: ${mono};
      font-size: 0.7em;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--text-disabled);
      font-weight: 500;
    }
    code {
      font-family: ${mono};
      font-size: 0.88em;
      padding: 1px 5px;
      background: rgba(0, 0, 0, 0.4);
      border-radius: 3px;
      color: #cfe9f4;
    }
    blockquote {
      border-left: 2px solid ${accent};
      padding-left: 0.85em;
      margin: 0.6em 0;
      color: var(--text-disabled);
    }
  }

  .footer {
    margin-top: 0.85rem;
    padding-top: 0.6rem;
    border-top: 1px solid ${borderSoft};
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
`;

const ContextChips = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  span {
    background: ${panelDeep};
    border: 1px solid ${borderSoft};
    border-radius: 3px;
    padding: 1px 6px;
  }
  strong { color: ${accent}; margin-left: 4px; }
`;

const CompetitorQATab = () => {
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState([]); // [{ question, answer, ts, summary }]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [activeCite, setActiveCite] = useState(null);
  const [activeRag, setActiveRag] = useState([]);

  const ask = async (q) => {
    const text = (q || '').trim();
    if (!text) return;
    setError(null);
    setLoading(true);
    try {
      const data = await askCompetitors(text);
      setHistory((h) => [
        {
          question: text,
          answer: data.answer,
          ts: new Date().toISOString(),
          summary: data.context_summary,
          sources_rag: data.sources_rag || [],
        },
        ...h,
      ]);
      setQuestion('');
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  const openCite = (cite, rag) => {
    setActiveRag(rag || []);
    setActiveCite(cite);
  };

  const copy = (text, idx) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    });
  };

  const fmtTs = (iso) => {
    const d = new Date(iso);
    return isNaN(d) ? '' : d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      <TechSection title="Q&A sobre competidores · IA con datos en vivo">
        <InputBar>
          <div>
            <TechInput
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && ask(question)}
              placeholder="Preguntá sobre competidores, sus debilidades, cobertura, movimientos…"
              disabled={loading}
            />
          </div>
          <TechButton type="button" onClick={() => ask(question)} disabled={loading || !question.trim()}>
            <FaPaperPlane /> {loading ? 'Consultando…' : 'Consultar'}
          </TechButton>
          <TechButtonGhost type="button" onClick={() => setHistory([])} disabled={!history.length}>
            <FaSyncAlt /> Limpiar
          </TechButtonGhost>
        </InputBar>

        <div style={{ marginBottom: '0.5rem', fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-disabled)' }}>
          Sugerencias
        </div>
        <Suggestions>
          {SUGGESTED.map((q, i) => (
            <Suggestion key={i} onClick={() => ask(q)} disabled={loading}>{q}</Suggestion>
          ))}
        </Suggestions>
      </TechSection>

      <TechSection title={history.length ? `Conversación · ${history.length} respuestas` : 'Respuestas'}>
        {loading && <Loading>La IA lee competidores y noticias recolectadas…</Loading>}
        {error && <ErrorBox>No se pudo consultar · {error}</ErrorBox>}
        {!loading && !error && history.length === 0 && (
          <Empty>
            Hacé una pregunta arriba o usá una sugerencia. La IA tiene acceso a:
            todos los competidores cargados, sus últimas 40 noticias scrapeadas y los
            insights IA semanales (`gaps` + `synthesis`).
          </Empty>
        )}

        {history.map((entry, idx) => {
          const handleCite = (cite) => openCite(cite, entry.sources_rag);
          return (
            <ConvoBlock key={idx}>
              <div className="question">
                <FaSearch style={{ marginRight: 6 }} /> {entry.question}
              </div>
              <div className="body">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={wrapWithCitations(handleCite)}
                >
                  {entry.answer}
                </ReactMarkdown>
              </div>
              <div className="footer">
                <ContextChips>
                  <span>contexto:</span>
                  <span>competidores <strong>{entry.summary?.competidores ?? 0}</strong></span>
                  <span>noticias <strong>{entry.summary?.noticias ?? 0}</strong></span>
                  <span>insights <strong>{entry.summary?.insights ?? 0}</strong></span>
                  {entry.summary?.rag_chunks > 0 && (
                    <span>RAG <strong>{entry.summary.rag_chunks}</strong></span>
                  )}
                </ContextChips>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span>{fmtTs(entry.ts)}</span>
                  <TechButtonGhost type="button" onClick={() => copy(entry.answer, idx)}>
                    {copiedIdx === idx ? <><FaCheck /> Copiado</> : <><FaCopy /> Copiar</>}
                  </TechButtonGhost>
                </div>
              </div>
            </ConvoBlock>
          );
        })}
      </TechSection>
      {activeCite && (
        <CitePopup
          cite={activeCite}
          indexed={[]}
          fresh={[]}
          rag={activeRag}
          onClose={() => setActiveCite(null)}
        />
      )}
    </>
  );
};

export default CompetitorQATab;
